import React from "react";
import { shallowEqual } from "react-redux";
import type { UscDispatchType } from "../../../../redux/store";
import { useUscSelector } from "../../../../redux/store";
import { setInsuranceQuote } from "../../../../redux/actions/UscInsuranceActions";
import { useUscLabel } from "../../../../utils/labels";
import { formatUscInsurancePrice, InsurancePriceLabelKey } from "../../../../utils/insuranceUtils";
import { UscDisclaimerType } from "../../../../../shared-logic/types/UscCommonTypes";
import { getAemInsuranceRateFormat } from "../../../../../../common-deprecated/settings/utils/financeSettingUtilsAem";
import { AemFmComponent } from "../../../../../../common-deprecated/utils/aemFlexibilityMatrixUtils";
import Panel from "../../styles/molecules/Panel";
import Text from "../../styles/atoms/Text";
import List from "../../styles/atoms/List";
import LinkTextDisclaimer from "../LinkTextDisclaimer";
import Icon from "../../styles/Icon";
import Button from "../../styles/atoms/Button";
import DisclaimerIconWrapper from "../DisclaimerIconWrapper";
import * as BasketStyles from "./UscBasket.styles";
import useUscInsuranceCalculatorButton from "../../../../hooks/uscUscInsuranceCalculatorButton";
import type { UscTrackingLocation } from "../../../../utils/tracking";
import { trackBasketRemoveItem, trackCta, uscStepTrackingLocationMap } from "../../../../utils/tracking";
import { enableUscInsurance } from "../../../../utils/settings";
import { useDispatch } from "../../../../../../shared-logic/redux/store";

const UscInsurancePanel = ({ trackingLocation }: { trackingLocation: UscTrackingLocation }): JSX.Element | null => {
    const dispatch = useDispatch<UscDispatchType>();
    const step = useUscSelector((state) => state.nav.step);
    const vehicleType = useUscSelector((state) => state.vehicle.type);
    const isActive = useUscSelector((state) => !!state.insurance.quote);
    const insurancePriceFormat = useUscSelector(({ commonSettings, vehicle }) =>
        getAemInsuranceRateFormat(commonSettings, AemFmComponent.Pdp, vehicle.type),
    );
    const { insurancePriceLabels } = useUscSelector(
        ({ insurance, commonSettings, uscSettings }) =>
            formatUscInsurancePrice(commonSettings, uscSettings, insurance.quote, insurancePriceFormat),
        shallowEqual,
    );
    const isInsuranceEnabled = useUscSelector(({ commonSettings, uscSettings, vehicle }) =>
        enableUscInsurance(commonSettings, uscSettings, vehicle.result, vehicle.type),
    );
    const [addQuoteLabel, editQuoteLabel, titleLabel, descriptionLabel, ariaRemoveInsuranceQuote] = useUscLabel([
        "uscInsuranceCalculatorButtonAdd",
        "uscInsuranceCalculatorButtonEdit",
        "uscBasketInsurance",
        "uscBasketInsuranceDescription",
        "uscAriaRemoveInsuranceQuote",
    ]);

    const { showInsuranceCalculator } = useUscInsuranceCalculatorButton();

    if (!isInsuranceEnabled) return null;

    const Header: JSX.Element = (
        <>
            <Text tag="h3" variant="h7">
                {titleLabel}
            </Text>
            <Button
                variant="icon-text"
                onClick={() => {
                    trackBasketRemoveItem(trackingLocation, vehicleType, "insurance");
                    dispatch(setInsuranceQuote(null));
                }}
                aria-label={ariaRemoveInsuranceQuote}
            >
                <Icon variant="close" />
            </Button>
        </>
    );

    return (
        <div>
            {isActive ? (
                <Panel variant="insurance" active={isActive} header={Header} data-testid="usc-insurancepanel">
                    <List variant="basket-panel-list">
                        <li>
                            {insurancePriceLabels.map(({ key, value }) => {
                                switch (key) {
                                    case InsurancePriceLabelKey.InsuranceType:
                                        return (
                                            <Text variant="body-small-semibold" key={key}>
                                                {value}
                                            </Text>
                                        );

                                    case InsurancePriceLabelKey.Price:
                                        return (
                                            <BasketStyles.InlineTextIcon key={key}>
                                                <Text variant="body-small-semibold">
                                                    <span dangerouslySetInnerHTML={{ __html: value }} />
                                                </Text>
                                                <DisclaimerIconWrapper type={UscDisclaimerType.Insurance} />
                                            </BasketStyles.InlineTextIcon>
                                        );

                                    case InsurancePriceLabelKey.CalculationId:
                                        return (
                                            <Text variant="body-small-regular" key={key}>
                                                {value}
                                            </Text>
                                        );

                                    case InsurancePriceLabelKey.Disclaimer:
                                        return <LinkTextDisclaimer type={UscDisclaimerType.Insurance} />;

                                    default:
                                        return null;
                                }
                            })}
                        </li>
                    </List>
                </Panel>
            ) : (
                <div>
                    <Text tag="h3" variant="h7">
                        {titleLabel}
                    </Text>
                    <Text tag="div" variant="body-small-book" color="grey-7">
                        {descriptionLabel}
                    </Text>
                </div>
            )}
            <Button
                variant="cta-large"
                onClick={() => {
                    trackCta("insurance-calculator", uscStepTrackingLocationMap[step], vehicleType);
                    showInsuranceCalculator();
                }}
            >
                <div>
                    <Icon variant="insurance" iconSize="medium" />
                    {isActive ? editQuoteLabel : addQuoteLabel}
                </div>
                <Icon variant="arrow-right" />
            </Button>
        </div>
    );
};

export default UscInsurancePanel;
